import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, Pencil, Trash2, Check, X, Star, StarOff, Power } from "lucide-react";

const inputCls = "w-full bg-obsidian border border-white/10 rounded-md text-white px-3 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none";

export default function MembershipAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", identifier: "", showAsRenewalContact: false });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", identifier: "" });

  const load = useCallback(async () => {
    try {
      setAdmins(await api.membershipAdmins());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar administradores");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.identifier.trim()) {
      toast.error("Nombre y número de WhatsApp son obligatorios");
      return;
    }
    setBusy(true);
    try {
      await api.createMembershipAdmin({
        name: form.name.trim(),
        identifier: form.identifier.trim(),
        showAsRenewalContact: form.showAsRenewalContact,
      });
      toast.success("Administrador agregado");
      setForm({ name: "", identifier: "", showAsRenewalContact: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al agregar administrador");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (a, field) => {
    try {
      await api.updateMembershipAdmin(a.id, { [field]: !a[field] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({ name: a.name, identifier: a.identifier });
  };

  const saveEdit = async (id) => {
    try {
      await api.updateMembershipAdmin(id, { name: editForm.name.trim(), identifier: editForm.identifier.trim() });
      toast.success("Administrador actualizado");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar al administrador "${a.name || a.identifier}"? Esto no afecta el acceso al panel web, solo el contacto de WhatsApp.`)) return;
    try {
      await api.deleteMembershipAdmin(a.id);
      toast.success("Administrador eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-surface p-6" data-testid="membership-admin-form-card">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="text-gold" size={18} />
          <h3 className="font-heading font-semibold">Agregar administrador</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Estos contactos son solo para avisos de WhatsApp. No otorgan acceso a este panel web.
        </p>
        <form onSubmit={create} className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="overline block mb-1.5">Nombre</label>
            <input data-testid="membership-admin-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej. Carlos R." maxLength={80} />
          </div>
          <div>
            <label className="overline block mb-1.5">WhatsApp (con código de país)</label>
            <input data-testid="membership-admin-phone-input" className={inputCls} value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value.replace(/[^\d]/g, "") })} placeholder="5215512345678" maxLength={20} />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground py-2.5">
            <input data-testid="membership-admin-renewal-checkbox" type="checkbox" checked={form.showAsRenewalContact} onChange={(e) => setForm({ ...form, showAsRenewalContact: e.target.checked })} className="w-4 h-4 accent-gold" />
            Contacto de renovación
          </label>
          <button data-testid="membership-admin-submit" disabled={busy} className="bg-gold text-black font-bold rounded-md px-6 py-2.5 text-sm hover:brightness-95 transition-all disabled:opacity-60">
            {busy ? "Agregando…" : "Agregar"}
          </button>
        </form>
      </div>

      <div className="card-surface overflow-hidden" data-testid="membership-admin-list">
        <div className="px-5 py-3 border-b border-white/10">
          <span className="overline">Administradores ({admins.length})</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : admins.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="membership-admin-empty">Aún no hay administradores registrados.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {admins.map((a) => (
              <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 hover:bg-white/[0.02]" data-testid={`membership-admin-row-${a.id}`}>
                {editingId === a.id ? (
                  <div className="flex-1 flex flex-col sm:flex-row gap-2">
                    <input data-testid={`membership-admin-edit-name-${a.id}`} className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                    <input data-testid={`membership-admin-edit-phone-${a.id}`} className={inputCls} value={editForm.identifier} onChange={(e) => setEditForm({ ...editForm, identifier: e.target.value.replace(/[^\d]/g, "") })} />
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {a.name || "Sin nombre"}
                      {!a.active && <span data-testid={`membership-admin-inactive-badge-${a.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-crimson/15 text-crimson border-crimson/30">Inactivo</span>}
                      {a.showAsRenewalContact && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-gold/15 text-gold border-gold/30">Contacto de renovación</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">+{a.identifier}</div>
                  </div>
                )}

                <div className="flex items-center gap-2 shrink-0">
                  {editingId === a.id ? (
                    <>
                      <button data-testid={`membership-admin-save-${a.id}`} onClick={() => saveEdit(a.id)} className="text-emerald-400 hover:bg-emerald-500/10 rounded-md p-1.5"><Check size={16} /></button>
                      <button data-testid={`membership-admin-cancel-${a.id}`} onClick={() => setEditingId(null)} className="text-muted-foreground hover:bg-white/5 rounded-md p-1.5"><X size={16} /></button>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid={`membership-admin-renewal-toggle-${a.id}`}
                        onClick={() => toggle(a, "showAsRenewalContact")}
                        title={a.showAsRenewalContact ? "Quitar como contacto de renovación" : "Marcar como contacto de renovación"}
                        className={`rounded-md p-1.5 border ${a.showAsRenewalContact ? "text-gold border-gold/40 bg-gold/10" : "text-muted-foreground border-white/10 hover:bg-white/5"}`}
                      >
                        {a.showAsRenewalContact ? <Star size={15} /> : <StarOff size={15} />}
                      </button>
                      <button
                        data-testid={`membership-admin-active-toggle-${a.id}`}
                        onClick={() => toggle(a, "active")}
                        title={a.active ? "Desactivar" : "Activar"}
                        className={`rounded-md p-1.5 border ${a.active ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-crimson border-crimson/30 bg-crimson/10"}`}
                      >
                        <Power size={15} />
                      </button>
                      <button data-testid={`membership-admin-edit-${a.id}`} onClick={() => startEdit(a)} className="text-muted-foreground hover:bg-white/5 rounded-md p-1.5"><Pencil size={15} /></button>
                      <button data-testid={`membership-admin-delete-${a.id}`} onClick={() => remove(a)} className="text-crimson hover:bg-crimson/10 rounded-md p-1.5"><Trash2 size={15} /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
